/*
 * THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.
 *
 * This file has been auto-generated. Any modifications
 * you make will be overwritten when the file is regenerated.
 *
 * For more information or questions, please go to #design-eng
 *
 */

/**
 * Typography tokens from Design Language 2.0 — literal resolved values.
 * Synced from Figma file: 5fZSvwd8naHgLN3UVZ5qFL
 *
 * CSS custom property variants live in typography-css-vars.ts.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FontFamily = 'body' | 'display' | 'mono';
export type FontSize = 10 | 12 | 14 | 16 | 18 | 20 | 22 | 24 | 28 | 32 | 36 | 42;
export type LineHeight = 10 | 12 | 14 | 16 | 18 | 20 | 24 | 28 | 32 | 36 | 42;
export type FontWeight = 'bold' | 'light' | 'medium' | 'regular';
export type LetterSpacing = 'ls-25' | 'ls-28' | 'ls-30' | 'ls-33' | 'ls-35' | 'ls-38' | 'ls-neg-5';
export type TypographyRampKey = 'bodyLMedium' | 'bodyLRegular' | 'bodyMMedium' | 'bodyMRegular' | 'bodySMedium' | 'bodySRegular' | 'headlineLBold' | 'headlineLMedium' | 'headlineLRegular' | 'headlineMBold' | 'headlineMMedium' | 'headlineMRegular' | 'headlineSBold' | 'headlineSMedium' | 'headlineSRegular' | 'headlineXlBold' | 'headlineXlMedium' | 'headlineXlRegular' | 'headlineXsBold' | 'headlineXsMedium' | 'headlineXsRegular' | 'headlineXxsBold' | 'headlineXxsMedium' | 'headlineXxsRegular';
export type MonoTypographyRampKey = 'monoBodyLMedium' | 'monoBodyLRegular' | 'monoBodyMMedium' | 'monoBodyMRegular' | 'monoBodySMedium' | 'monoBodySRegular' | 'monoHeadlineLBold' | 'monoHeadlineLMedium' | 'monoHeadlineLRegular' | 'monoHeadlineMBold' | 'monoHeadlineMMedium' | 'monoHeadlineMRegular' | 'monoHeadlineSBold' | 'monoHeadlineSMedium' | 'monoHeadlineSRegular' | 'monoHeadlineXlBold' | 'monoHeadlineXlMedium' | 'monoHeadlineXlRegular' | 'monoHeadlineXsBold' | 'monoHeadlineXsMedium' | 'monoHeadlineXsRegular' | 'monoHeadlineXxsBold' | 'monoHeadlineXxsMedium' | 'monoHeadlineXxsRegular';
export type TypographyStyle = {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  fontWeight: number | string;
  letterSpacing: string;
};

// ---------------------------------------------------------------------------
// Font families
// ---------------------------------------------------------------------------

/** Font family tokens — literal resolved values. */
export const fontFamilies = ({
  body: 'UberMoveText',
  display: 'UberMove',
  mono: 'UberMoveMono'
} as const);

// ---------------------------------------------------------------------------
// Font sizes
// ---------------------------------------------------------------------------

/**
 * Font size tokens — literal rem values (16px = 1rem base).
 * Keys are the px step values from the DL 2.0 scale.
 */
export const fontSizes = ({
  10: 'calc(10/16*1rem)',
  12: 'calc(12/16*1rem)',
  14: 'calc(14/16*1rem)',
  16: '1rem',
  18: 'calc(18/16*1rem)',
  20: 'calc(20/16*1rem)',
  22: 'calc(22/16*1rem)',
  24: 'calc(24/16*1rem)',
  28: 'calc(28/16*1rem)',
  32: 'calc(32/16*1rem)',
  36: 'calc(36/16*1rem)',
  42: 'calc(42/16*1rem)'
} as const);

// ---------------------------------------------------------------------------
// Line heights
// ---------------------------------------------------------------------------

/**
 * Line height tokens — literal rem values (16px = 1rem base).
 * Keys are the associated font-size step; each entry gives the paired line height.
 * e.g. lineHeights[16] is the line height to use alongside fontSizes[16].
 * Note: 22px has no paired line height entry.
 */
export const lineHeights = ({
  10: 'calc(12/16*1rem)',
  12: '1rem',
  14: 'calc(18/16*1rem)',
  16: 'calc(20/16*1rem)',
  18: 'calc(22/16*1rem)',
  20: 'calc(24/16*1rem)',
  24: 'calc(28/16*1rem)',
  28: 'calc(32/16*1rem)',
  32: 'calc(36/16*1rem)',
  36: 'calc(40/16*1rem)',
  42: 'calc(48/16*1rem)'
} as const);

// ---------------------------------------------------------------------------
// Font weights
// ---------------------------------------------------------------------------

/** Font weight tokens — literal numeric CSS values, ordered light → bold. */
export const fontWeights = ({
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700
} as const);

// ---------------------------------------------------------------------------
// Letter spacings
// ---------------------------------------------------------------------------

/** Letter spacing tokens — literal values in pixels. */
export const letterSpacings = ({
  'ls-25': 0.25,
  'ls-28': 0.28,
  'ls-30': 0.3,
  'ls-33': 0.33,
  'ls-35': 0.35,
  'ls-38': 0.38,
  'ls-neg-5': -0.05
} as const);

/** Font weight numeric mapping — maps Figma weight names to CSS numeric values. */
export const fontWeightNumeric = ({
  Light: 300,
  Regular: 400,
  Medium: 500,
  Bold: 700
} as const);

// ---------------------------------------------------------------------------
// Typography ramp composites
// ---------------------------------------------------------------------------

/**
 * Literal resolved typography values.
 * Use for SSR, static styles, or anywhere CSS variables are unavailable.
 */
export const typographyRamp = (({
  bodyLMedium: {
    fontFamily: 'UberMoveText',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 500,
    letterSpacing: '-0.05px'
  },
  bodyLRegular: {
    fontFamily: 'UberMoveText',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 400,
    letterSpacing: '-0.05px'
  },
  bodyMMedium: {
    fontFamily: 'UberMoveText',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 500,
    letterSpacing: '-0.05px'
  },
  bodyMRegular: {
    fontFamily: 'UberMoveText',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 400,
    letterSpacing: '-0.05px'
  },
  bodySMedium: {
    fontFamily: 'UberMoveText',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 500,
    letterSpacing: '-0.05px'
  },
  bodySRegular: {
    fontFamily: 'UberMoveText',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 400,
    letterSpacing: '-0.05px'
  },
  headlineLBold: {
    fontFamily: 'UberMove',
    fontSize: 'calc(20/16*1rem)',
    lineHeight: 'calc(24/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0.35px'
  },
  headlineLMedium: {
    fontFamily: 'UberMove',
    fontSize: 'calc(20/16*1rem)',
    lineHeight: 'calc(24/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0.3px'
  },
  headlineLRegular: {
    fontFamily: 'UberMove',
    fontSize: 'calc(20/16*1rem)',
    lineHeight: 'calc(24/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0.25px'
  },
  headlineMBold: {
    fontFamily: 'UberMove',
    fontSize: 'calc(18/16*1rem)',
    lineHeight: 'calc(22/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0.35px'
  },
  headlineMMedium: {
    fontFamily: 'UberMove',
    fontSize: 'calc(18/16*1rem)',
    lineHeight: 'calc(22/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0.3px'
  },
  headlineMRegular: {
    fontFamily: 'UberMove',
    fontSize: 'calc(18/16*1rem)',
    lineHeight: 'calc(22/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0.25px'
  },
  headlineSBold: {
    fontFamily: 'UberMove',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0.35px'
  },
  headlineSMedium: {
    fontFamily: 'UberMove',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0.3px'
  },
  headlineSRegular: {
    fontFamily: 'UberMove',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0.25px'
  },
  headlineXlBold: {
    fontFamily: 'UberMove',
    fontSize: 'calc(24/16*1rem)',
    lineHeight: 'calc(28/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0.35px'
  },
  headlineXlMedium: {
    fontFamily: 'UberMove',
    fontSize: 'calc(24/16*1rem)',
    lineHeight: 'calc(28/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0.3px'
  },
  headlineXlRegular: {
    fontFamily: 'UberMove',
    fontSize: 'calc(24/16*1rem)',
    lineHeight: 'calc(28/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0.25px'
  },
  headlineXsBold: {
    fontFamily: 'UberMove',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0.38px'
  },
  headlineXsMedium: {
    fontFamily: 'UberMove',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0.33px'
  },
  headlineXsRegular: {
    fontFamily: 'UberMove',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0.28px'
  },
  headlineXxsBold: {
    fontFamily: 'UberMove',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 700,
    letterSpacing: '0.38px'
  },
  headlineXxsMedium: {
    fontFamily: 'UberMove',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 500,
    letterSpacing: '0.33px'
  },
  headlineXxsRegular: {
    fontFamily: 'UberMove',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 400,
    letterSpacing: '0.28px'
  }
} as const) satisfies Record<TypographyRampKey, TypographyStyle>);

/**
 * Mono typography ramp — same scale as the standard ramp.
 * Font family is UberMoveMono and letter spacing is always 0.
 */
export const monoTypographyRamp = (({
  monoBodyLMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoBodyLRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoBodyMMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoBodyMRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoBodySMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoBodySRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoHeadlineLBold: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(20/16*1rem)',
    lineHeight: 'calc(24/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0px'
  },
  monoHeadlineLMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(20/16*1rem)',
    lineHeight: 'calc(24/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoHeadlineLRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(20/16*1rem)',
    lineHeight: 'calc(24/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoHeadlineMBold: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(18/16*1rem)',
    lineHeight: 'calc(22/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0px'
  },
  monoHeadlineMMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(18/16*1rem)',
    lineHeight: 'calc(22/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoHeadlineMRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(18/16*1rem)',
    lineHeight: 'calc(22/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoHeadlineSBold: {
    fontFamily: 'UberMoveMono',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0px'
  },
  monoHeadlineSMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoHeadlineSRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: '1rem',
    lineHeight: 'calc(20/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoHeadlineXlBold: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(24/16*1rem)',
    lineHeight: 'calc(28/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0px'
  },
  monoHeadlineXlMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(24/16*1rem)',
    lineHeight: 'calc(28/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoHeadlineXlRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(24/16*1rem)',
    lineHeight: 'calc(28/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoHeadlineXsBold: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 700,
    letterSpacing: '0px'
  },
  monoHeadlineXsMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoHeadlineXsRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(14/16*1rem)',
    lineHeight: 'calc(18/16*1rem)',
    fontWeight: 400,
    letterSpacing: '0px'
  },
  monoHeadlineXxsBold: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 700,
    letterSpacing: '0px'
  },
  monoHeadlineXxsMedium: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 500,
    letterSpacing: '0px'
  },
  monoHeadlineXxsRegular: {
    fontFamily: 'UberMoveMono',
    fontSize: 'calc(12/16*1rem)',
    lineHeight: '1rem',
    fontWeight: 400,
    letterSpacing: '0px'
  }
} as const) satisfies Record<MonoTypographyRampKey, TypographyStyle>);